import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BadgeDollarSign, CheckCircle2, CircleAlert, Cloud, KeyRound,
  Loader2, RefreshCw, Trash2, TrendingUp,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend,
} from "recharts";

const errMsg = (e, fallback) => e.response?.data?.detail || fallback;

const fmtMoney = (v, ccy = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: ccy || "USD",
    maximumFractionDigits: 2,
  }).format(v || 0);

const monthLabel = (iso) => {
  if (!iso) return "";
  try {
    const [y, m] = iso.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("en-AU", { month: "short", year: "2-digit" });
  } catch {
    return iso;
  }
};

/* ── IAM settings dialog ────────────────────────────────────────── */
function SettingsDialog({ open, onClose, settings, reload }) {
  const [keyId, setKeyId] = useState("");
  const [secret, setSecret] = useState("");
  const [label, setLabel] = useState(settings?.label || "");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (open) {
      setKeyId(""); setSecret(""); setLabel(settings?.label || "");
    }
  }, [open, settings?.label]);

  const save = async () => {
    if (keyId.length < 16 || secret.length < 16) {
      toast.error("Enter both AWS Access Key ID and Secret Access Key");
      return;
    }
    setSaving(true);
    try {
      await api.post("/aws-cost/settings", {
        access_key_id: keyId.trim(), secret_access_key: secret.trim(),
        label: label || null,
      });
      toast.success("AWS credentials saved (encrypted at rest)");
      await reload();
      onClose();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/aws-cost/test");
      toast.success(`Connected · ${data.services_seen} service(s) seen`);
      await reload();
    } catch (e) { toast.error(errMsg(e, "Test failed")); }
    finally { setTesting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="aws-cost-settings-dialog">
        <DialogHeader>
          <DialogTitle>AWS Cost Explorer credentials</DialogTitle>
          <DialogDescription>
            One master IAM user with a read-only Cost Explorer policy — used
            for all Host Servers billing views. Keys are encrypted at rest.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-sky-200 bg-sky-50/60 p-3 text-xs text-sky-900 space-y-1.5">
          <div className="font-semibold">Required IAM permissions (attach to the user):</div>
          <div className="font-mono bg-white/70 border border-sky-100 rounded px-2 py-1.5 leading-5">
            ce:GetCostAndUsage<br />
            ce:GetCostForecast<br />
            ce:GetDimensionValues
          </div>
          <div className="pt-1">
            Cost Explorer lives only in <b>us-east-1</b> and charges $0.01
            per API call — we cache all responses for 6 hours.
          </div>
        </div>

        <div className="space-y-3">
          {settings?.configured && (
            <div className="text-xs text-zinc-500">
              Current key: <span className="font-mono">••••{settings.access_key_id_last4}</span>
              {settings.label && <> · <span className="italic">{settings.label}</span></>}
            </div>
          )}
          <Input placeholder="AWS Access Key ID (starts with AKIA…)"
            value={keyId} onChange={(e) => setKeyId(e.target.value)}
            data-testid="aws-cost-access-key-input" />
          <Input type="password"
            placeholder={settings?.configured ? "New Secret Access Key (required to rotate)" : "AWS Secret Access Key"}
            value={secret} onChange={(e) => setSecret(e.target.value)}
            data-testid="aws-cost-secret-key-input" />
          <Input placeholder="Label (optional, e.g. Iceberg-Master-CE-Reader)"
            value={label} onChange={(e) => setLabel(e.target.value)}
            data-testid="aws-cost-label-input" />
        </div>

        <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
          {settings?.configured && (
            <Button variant="outline" onClick={test} disabled={testing}
              data-testid="aws-cost-test-btn">
              {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Test connection
            </Button>
          )}
          <Button onClick={save} disabled={saving || keyId.length < 16 || secret.length < 16}
            data-testid="aws-cost-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save credentials
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Empty / not-configured banner ─────────────────────────────── */
function EmptyState({ onOpen }) {
  return (
    <Card>
      <CardContent className="p-8 text-center space-y-3">
        <BadgeDollarSign className="h-8 w-8 text-sky-500 mx-auto" />
        <div className="text-lg font-semibold">AWS Cost Explorer isn&apos;t connected yet</div>
        <div className="text-sm text-zinc-500 max-w-md mx-auto">
          Add a read-only IAM user&apos;s Access Key + Secret to pull monthly
          costs, top services, and a 12-month forecast for your AWS bill.
        </div>
        <Button onClick={onOpen} data-testid="aws-cost-connect-btn">
          <KeyRound className="h-4 w-4 mr-1" />Connect AWS
        </Button>
      </CardContent>
    </Card>
  );
}

/* ── Main tab ───────────────────────────────────────────────────── */
export default function HostAwsCostTab() {
  const [settings, setSettings] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/aws-cost/settings");
      setSettings(data);
    } catch { setSettings({ configured: false }); }
  }, []);

  const loadSummary = useCallback(async (refresh = false) => {
    try {
      const { data } = await api.get("/aws-cost/summary",
        { params: refresh ? { refresh: true } : {} });
      setSummary(data);
    } catch (e) {
      if (e.response?.status !== 400) toast.error(errMsg(e, "Could not load AWS costs"));
      setSummary(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadSettings();
      await loadSummary(false);
      setLoading(false);
    })();
  }, [loadSettings, loadSummary]);

  const refresh = async () => {
    setRefreshing(true);
    await loadSummary(true);
    setRefreshing(false);
  };

  const disconnect = async () => {
    if (!window.confirm("Remove AWS Cost Explorer credentials and cached snapshots?")) return;
    setDeleting(true);
    try {
      await api.delete("/aws-cost/settings");
      toast.success("AWS credentials removed");
      setSummary(null);
      await loadSettings();
    } catch (e) { toast.error(errMsg(e, "Delete failed")); }
    finally { setDeleting(false); }
  };

  const currency = summary?.currency || "USD";
  const chartData = useMemo(() =>
    (summary?.months || []).map((m) => ({
      month: monthLabel(m.month), total: m.total,
    })), [summary]);

  const forecastData = useMemo(() =>
    (summary?.forecast?.points || []).map((p) => ({
      month: monthLabel(p.month), amount: p.amount,
    })), [summary]);

  const current = summary?.current_month;
  const prev = summary?.months && summary.months.length > 1
    ? summary.months[summary.months.length - 2] : null;
  const delta = current && prev && prev.total > 0
    ? ((current.total - prev.total) / prev.total) * 100 : null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-500 p-6"
        data-testid="aws-cost-loading">
        <Loader2 className="h-4 w-4 animate-spin" />Loading AWS Cost Explorer…
      </div>
    );
  }

  if (!settings?.configured) {
    return (
      <>
        <EmptyState onOpen={() => setDialogOpen(true)} />
        <SettingsDialog open={dialogOpen} onClose={() => setDialogOpen(false)}
          settings={settings} reload={async () => { await loadSettings(); await loadSummary(false); }} />
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="aws-cost-tab">
      {/* Header bar */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Cloud className="h-5 w-5 text-sky-600 shrink-0" />
          <div className="flex-1 min-w-[240px]">
            <div className="text-sm font-semibold flex items-center gap-2">
              AWS Cost Explorer connected
              {settings.status === "ok" ? (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200"
                  data-testid="aws-cost-status-ok">
                  <CheckCircle2 className="h-3 w-3 mr-1" />OK
                </Badge>
              ) : settings.status === "error" ? (
                <Badge className="bg-red-50 text-red-700 border-red-200">
                  <CircleAlert className="h-3 w-3 mr-1" />error
                </Badge>
              ) : (
                <Badge variant="outline">unchecked</Badge>
              )}
            </div>
            <div className="text-xs text-zinc-500">
              Key ••••{settings.access_key_id_last4}
              {settings.label && <> · {settings.label}</>}
              {settings.last_checked && <> · last checked {new Date(settings.last_checked).toLocaleString()}</>}
            </div>
            {settings.status_detail && (
              <div className="text-xs text-zinc-500 mt-0.5">{settings.status_detail}</div>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}
            data-testid="aws-cost-refresh-btn">
            {refreshing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}
            data-testid="aws-cost-edit-btn">
            <KeyRound className="h-4 w-4 mr-1" />Rotate keys
          </Button>
          <Button variant="ghost" size="sm" onClick={disconnect} disabled={deleting}
            className="text-red-600 hover:text-red-700"
            data-testid="aws-cost-disconnect-btn">
            <Trash2 className="h-4 w-4 mr-1" />Disconnect
          </Button>
        </CardContent>
      </Card>

      {!summary ? (
        <Card><CardContent className="p-6 text-sm text-zinc-500 space-y-2">
          <div>No cost data yet — click <b>Refresh</b> to pull the last 6 months from AWS.</div>
          {settings.status === "error" && settings.status_detail && (
            <div className="text-xs text-red-600 border border-red-200 bg-red-50/60 rounded p-2">
              Last AWS call failed: {settings.status_detail}
            </div>
          )}
        </CardContent></Card>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card>
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wide text-zinc-500">This month so far</div>
                <div className="text-2xl font-bold mt-1" data-testid="aws-cost-kpi-current">
                  {fmtMoney(current?.total, currency)}
                </div>
                {delta != null && (
                  <div className={`text-xs mt-1 ${delta >= 0 ? "text-red-600" : "text-emerald-700"}`}>
                    {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}% vs previous month
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wide text-zinc-500">Previous month</div>
                <div className="text-2xl font-bold mt-1" data-testid="aws-cost-kpi-previous">
                  {fmtMoney(prev?.total, currency)}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {prev ? monthLabel(prev.month) : "—"}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1">
                  <TrendingUp className="h-3.5 w-3.5" />12-month forecast
                </div>
                <div className="text-2xl font-bold mt-1" data-testid="aws-cost-kpi-forecast">
                  {fmtMoney(summary.forecast?.total, summary.forecast?.currency || currency)}
                </div>
                {summary.forecast?.error && (
                  <div className="text-xs text-amber-600 mt-1">Forecast unavailable: {summary.forecast.error}</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Monthly bar chart */}
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Monthly cost — last 6 months</div>
                <div className="text-xs text-zinc-500">
                  {summary.from_cache ? "cached (refresh for latest)" : "live from AWS"}
                </div>
              </div>
              {chartData.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }}
                      tickFormatter={(v) => fmtMoney(v, currency).replace(/\.\d+/, "")} />
                    <Tooltip formatter={(v) => fmtMoney(v, currency)} />
                    <Legend />
                    <Bar dataKey="total" name="Cost" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-sm text-zinc-500 py-6 text-center">No months returned.</div>
              )}
            </CardContent>
          </Card>

          {/* Forecast line chart */}
          {forecastData.length > 0 && (
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="text-sm font-semibold">Projected cost — next 12 months</div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={forecastData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }}
                      tickFormatter={(v) => fmtMoney(v, summary.forecast?.currency || currency).replace(/\.\d+/, "")} />
                    <Tooltip formatter={(v) => fmtMoney(v, summary.forecast?.currency || currency)} />
                    <Line type="monotone" dataKey="amount" name="Forecast"
                      stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Top services table */}
          {current?.top_services?.length > 0 && (
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="text-sm font-semibold">
                  Top services · {monthLabel(current.month)}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="aws-cost-top-services-table">
                    <thead>
                      <tr className="text-left text-xs uppercase text-zinc-500 border-b">
                        <th className="py-2 pr-4">Service</th>
                        <th className="py-2 pr-4 text-right">Cost</th>
                        <th className="py-2 pr-4 text-right">% of month</th>
                      </tr>
                    </thead>
                    <tbody>
                      {current.top_services.map((s) => {
                        const pct = current.total > 0
                          ? (s.amount / current.total) * 100 : 0;
                        return (
                          <tr key={s.service} className="border-b last:border-b-0">
                            <td className="py-2 pr-4 font-medium">{s.service}</td>
                            <td className="py-2 pr-4 text-right tabular-nums">
                              {fmtMoney(s.amount, currency)}
                            </td>
                            <td className="py-2 pr-4 text-right text-zinc-500 tabular-nums">
                              {pct.toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <SettingsDialog open={dialogOpen} onClose={() => setDialogOpen(false)}
        settings={settings} reload={async () => { await loadSettings(); await loadSummary(false); }} />
    </div>
  );
}
